import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { MetricsService } from '../services/metrics/MetricsService';
const MAX_HISTORY_POINTS = 100; // Keep more history for better visualization
const CHART_HEIGHT = 80;
const CHART_WIDTH = 400;
const CHART_PADDING = 40; // Padding for labels
// Generate consistent colors for metrics
function getMetricColor(metricName) {
    const colors = [
        '#2196F3', // Blue
        '#4CAF50', // Green
        '#FF9800', // Orange
        '#9C27B0', // Purple
        '#F44336', // Red
        '#00BCD4', // Cyan
        '#FFEB3B', // Yellow
        '#795548', // Brown
        '#607D8B', // Blue Grey
        '#E91E63', // Pink
    ];
    // Simple hash function to get consistent color for metric name
    let hash = 0;
    for (let i = 0; i < metricName.length; i++) {
        hash = metricName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}
function formatMetricName(metricName) {
    // Convert camelCase to Title Case with spaces
    return metricName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}
function LineGraph({ data, metricName, }) {
    const color = getMetricColor(metricName);
    if (data.length < 2) {
        return (_jsx("div", { style: {
                width: CHART_WIDTH + CHART_PADDING * 2,
                height: CHART_HEIGHT + CHART_PADDING,
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '12px',
            }, children: "Waiting for data..." }));
    }
    // Calculate min/max for auto-scaling
    const values = data.map((d) => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    const padding = range * 0.1; // 10% padding
    const chartMin = Math.max(0, minValue - padding);
    const chartMax = maxValue + padding;
    const chartRange = chartMax - chartMin || 1; // Avoid division by zero
    const points = data
        .map((point, index) => {
        const x = (index / (data.length - 1)) * CHART_WIDTH + CHART_PADDING;
        const normalizedValue = (point.value - chartMin) / chartRange;
        const y = CHART_HEIGHT - normalizedValue * CHART_HEIGHT;
        return `${x},${y}`;
    })
        .join(' ');
    // Calculate time range for x-axis labels
    const timeSpan = data[data.length - 1].timestamp - data[0].timestamp;
    const timeLabel = timeSpan > 0 ? `${(timeSpan / 1000).toFixed(0)}s` : '0s';
    return (_jsxs("svg", { width: CHART_WIDTH + CHART_PADDING * 2, height: CHART_HEIGHT + CHART_PADDING, style: {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
        }, role: "img", "aria-label": `${metricName} history chart`, children: [[0, 0.25, 0.5, 0.75, 1].map((ratio) => (_jsx("line", { x1: CHART_PADDING, y1: CHART_HEIGHT * (1 - ratio), x2: CHART_WIDTH + CHART_PADDING, y2: CHART_HEIGHT * (1 - ratio), stroke: "#f0f0f0", strokeWidth: "1" }, ratio))), _jsx("line", { x1: CHART_PADDING, y1: 0, x2: CHART_PADDING, y2: CHART_HEIGHT, stroke: "#ccc", strokeWidth: "1" }), _jsx("line", { x1: CHART_PADDING, y1: CHART_HEIGHT, x2: CHART_WIDTH + CHART_PADDING, y2: CHART_HEIGHT, stroke: "#ccc", strokeWidth: "1" }), [
                { ratio: 1, value: chartMax },
                { ratio: 0.5, value: (chartMax + chartMin) / 2 },
                { ratio: 0, value: chartMin },
            ].map(({ ratio, value }) => (_jsx("text", { x: CHART_PADDING - 8, y: CHART_HEIGHT * (1 - ratio) + 4, textAnchor: "end", fontSize: "10", fill: "#666", children: value.toFixed(0) }, ratio))), _jsxs("text", { x: CHART_PADDING, y: CHART_HEIGHT + 20, textAnchor: "start", fontSize: "10", fill: "#666", children: ["-", timeLabel] }), _jsx("text", { x: CHART_WIDTH + CHART_PADDING, y: CHART_HEIGHT + 20, textAnchor: "end", fontSize: "10", fill: "#666", children: "now" }), _jsx("defs", { children: _jsxs("linearGradient", { id: `gradient-${metricName}`, x1: "0%", y1: "0%", x2: "0%", y2: "100%", children: [_jsx("stop", { offset: "0%", stopColor: color, stopOpacity: "0.3" }), _jsx("stop", { offset: "100%", stopColor: color, stopOpacity: "0.05" })] }) }), _jsx("polygon", { points: `${CHART_PADDING},${CHART_HEIGHT} ${points} ${CHART_WIDTH + CHART_PADDING},${CHART_HEIGHT}`, fill: `url(#gradient-${metricName})` }), _jsx("polyline", { points: points, fill: "none", stroke: color, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: ((data.length - 1) / (data.length - 1)) * CHART_WIDTH +
                    CHART_PADDING, cy: CHART_HEIGHT -
                    ((data[data.length - 1].value - chartMin) / chartRange) *
                        CHART_HEIGHT, r: "4", fill: color, stroke: "white", strokeWidth: "2" })] }));
}
export function MetricsDisplay() {
    const metrics = MetricsService.getOutput().useMetrics();
    const newDataPoints = MetricsService.getOutput().useNewDataPoints();
    // Store history for all metrics dynamically
    const [metricsHistory, setMetricsHistory] = React.useState({});
    // Update history when new data points arrive
    React.useEffect(() => {
        if (Object.keys(newDataPoints).length === 0) {
            return; // No new data points
        }
        setMetricsHistory((prev) => {
            const updated = { ...prev };
            // Append all new data points to history
            Object.entries(newDataPoints).forEach(([metricName, points]) => {
                const history = updated[metricName] || [];
                const newHistory = [
                    ...history,
                    ...points.map((p) => ({
                        value: p.value,
                        timestamp: p.recordedAt,
                    })),
                ];
                updated[metricName] = newHistory.slice(-MAX_HISTORY_POINTS);
            });
            return updated;
        });
    }, [newDataPoints]);
    // Get sorted list of metric names (from both current metrics and history)
    const allMetricNames = new Set([
        ...Object.keys(metrics),
        ...Object.keys(metricsHistory),
    ]);
    const metricNames = Array.from(allMetricNames).sort();
    return (_jsxs("div", { className: "metrics-display", style: {
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            minWidth: '500px',
            maxWidth: '600px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }, children: [_jsxs("h3", { style: {
                    margin: '0 0 16px 0',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#333',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#f5f5f5',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #ddd',
                    zIndex: 1,
                }, children: ["Real-time Metrics", metricNames.length > 0 && (_jsxs("span", { style: {
                            marginLeft: '12px',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            color: '#666',
                        }, children: ["(", metricNames.length, ' ', metricNames.length === 1 ? 'metric' : 'metrics', ")"] }))] }), _jsx("div", { style: {
                    maxHeight: '600px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '8px',
                }, children: metricNames.length === 0 ? (_jsx("div", { style: {
                        padding: '40px 20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '14px',
                    }, children: "No metrics available yet..." })) : (metricNames.map((metricName) => {
                    const currentValue = metrics[metricName] || 0;
                    const history = metricsHistory[metricName] || [];
                    const color = getMetricColor(metricName);
                    return (_jsxs("div", { style: {
                            marginBottom: '24px',
                            padding: '16px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        }, children: [_jsxs("div", { style: {
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                }, children: [_jsx("span", { style: {
                                            fontWeight: 'bold',
                                            color: color,
                                            fontSize: '14px',
                                        }, children: formatMetricName(metricName) }), _jsxs("span", { style: {
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: '#333',
                                            fontFamily: 'monospace',
                                        }, children: [currentValue.toFixed(2), metricName.includes('Milliseconds') &&
                                                'ms'] })] }), _jsx(LineGraph, { data: history, metricName: metricName }), history.length > 1 && (_jsxs("div", { style: {
                                    display: 'flex',
                                    gap: '16px',
                                    marginTop: '8px',
                                    fontSize: '11px',
                                    color: '#666',
                                }, children: [_jsxs("span", { children: ["Min:", ' ', Math.min(...history.map((h) => h.value)).toFixed(2)] }), _jsxs("span", { children: ["Max:", ' ', Math.max(...history.map((h) => h.value)).toFixed(2)] }), _jsxs("span", { children: ["Avg:", ' ', (history.reduce((sum, h) => sum + h.value, 0) / history.length).toFixed(2)] })] }))] }, metricName));
                })) })] }));
}
